import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { authApi } from "./authApi";

const LS_KEY = "userDetails";

// ─── JWT helpers ─────────────────────────────────────────────────────────────

const decodeJwtExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.exp ? new Date(payload.exp * 1000) : null;
  } catch {
    return null;
  }
};

const extractUserFromJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]))?.user ?? null;
  } catch {
    return null;
  }
};

// ─── Synchronous localStorage hydration (runs once at module load) ───────────
// This replaces the old checkAutoLogin thunk. The store is already seeded with
// the persisted session before the first React render, so App.jsx has the right
// isAuthenticated value immediately — no useEffect required.

const loadPersistedAuth = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored?.idToken) return null;
    const expireDate = new Date(stored.expireDate);
    if (isNaN(expireDate.getTime()) || new Date() > expireDate) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    // Restore the Authorization header so direct Axios calls (mutations in
    // components) are also authenticated without extra wiring.
    axios.defaults.headers.common["Authorization"] = `Bearer ${stored.idToken}`;
    if (!stored.modules) stored.modules = {};
    if (!stored.pagePermissions) stored.pagePermissions = {};
    return stored;
  } catch {
    return null;
  }
};

const persisted = loadPersistedAuth();

// ─── Slice ────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: persisted?.idToken ?? null,
    user: persisted
      ? {
          name: persisted.name,
          email: persisted.email,
          role: persisted.role,
          localId: persisted.localId,
          projectIds: persisted.projectIds ?? [],
          modules: persisted.modules ?? {},
          pagePermissions: persisted.pagePermissions ?? {},
        }
      : null,
    expireDate: persisted?.expireDate ?? null,
    isAuthenticated: !!persisted?.idToken,
  },
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;
      state.expireDate = null;
      state.isAuthenticated = false;
      // Clear all localStorage — wipes any keys from older code paths too
      localStorage.clear();
      delete axios.defaults.headers.common["Authorization"];
    },
    setModules(state, { payload }) {
      if (state.user) {
        state.user.modules = payload;
        try {
          const raw = localStorage.getItem(LS_KEY);
          if (raw) {
            const stored = JSON.parse(raw);
            stored.modules = payload;
            localStorage.setItem(LS_KEY, JSON.stringify(stored));
          }
        } catch {}
      }
    },
    refreshModules(state, { payload }) {
      if (!state.user) return;
      const { modules, projectIds, role, pagePermissions } = payload;
      if (modules) state.user.modules = modules;
      if (role) state.user.role = role;
      if (pagePermissions !== undefined) state.user.pagePermissions = pagePermissions;
      if (projectIds) {
        state.user.projectIds = projectIds.map((p) =>
          typeof p === "object" ? p._id?.toString() ?? p.id?.toString() : p.toString()
        );
      }
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
          const stored = JSON.parse(raw);
          if (modules) stored.modules = modules;
          if (role) stored.role = role;
          if (pagePermissions !== undefined) stored.pagePermissions = pagePermissions;
          if (projectIds) stored.projectIds = state.user.projectIds;
          localStorage.setItem(LS_KEY, JSON.stringify(stored));
        }
      } catch {}
    },
  },
  extraReducers: (builder) => {
    // Fires after a successful POST /login response — single source of truth
    // for persisting the session. No component needs to manually dispatch.
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        const token = payload.token;
        const expireDate =
          decodeJwtExpiry(token) ??
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        const expireDateISO = expireDate.toISOString();

        const jwtUser = extractUserFromJwt(token);
        const user = {
          name: jwtUser?.name ?? "",
          email: jwtUser?.email ?? "",
          role: jwtUser?.role ?? payload.role ?? "",
          localId: jwtUser?.id ?? jwtUser?._id ?? "",
          projectIds: (jwtUser?.projectIds ?? []).map((p) =>
            typeof p === "object" ? p._id?.toString() ?? p.id?.toString() : p.toString()
          ),
          modules: jwtUser?.modules ?? {},
          pagePermissions: jwtUser?.pagePermissions ?? {},
        };

        state.token = token;
        state.expireDate = expireDateISO;
        state.user = user;
        state.isAuthenticated = true;

        localStorage.setItem(
          LS_KEY,
          JSON.stringify({
            idToken: token,
            expireDate: expireDateISO,
            name: user.name,
            email: user.email,
            role: user.role,
            localId: user.localId,
            projectIds: user.projectIds,
            modules: user.modules,
            pagePermissions: user.pagePermissions,
          }),
        );

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      },
    );
  },
});

export const { logout, setModules, refreshModules } = authSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectToken           = (state) => state.auth.token;
export const selectUser            = (state) => state.auth.user;
export const selectExpireDate      = (state) => state.auth.expireDate;
export const selectUserRole            = (state) => state.auth.user?.role ?? "";
export const selectUserModules         = (state) => state.auth.user?.modules ?? {};
export const selectUserPagePermissions = (state) => state.auth.user?.pagePermissions ?? {};

const LEVELS = { none: 0, read: 1, "read-write": 2 };

/**
 * selectHasModuleAccess(moduleName, level)
 * Returns a memoisation-friendly selector factory.
 * Usage: useSelector(selectHasModuleAccess("finance", "read"))
 */
export const selectHasModuleAccess = (moduleName, level = "read") => (state) => {
  const role = state.auth.user?.role;
  if (role === "super-admin") return true;
  const modules = state.auth.user?.modules ?? {};
  return (LEVELS[modules[moduleName] ?? "none"] ?? 0) >= (LEVELS[level] ?? 0);
};

export default authSlice.reducer;
